import React, { useState } from 'react';
import ProposalSectionView from './ProposalSectionView';
import SectionCommentThread from './SectionCommentThread';

const ProposalSectionCard = ({
  section,
  workers = [],
  comments = [],
  clientComments = [],
  onAddComment,
  onDeleteComment,
  onUpdateContent,
  onRequestRevision,
  commentLoading = false,
  isActive = false,
  onSelect,
}) => {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const sectionNumber = section?.section_number;
  const isEdited = section?.manually_edited;
  const openComments = comments.filter(
    (c) => c.section_number === sectionNumber && c.status === 'open'
  ).length;

  const handleStartEdit = () => {
    // Flatten content to a string for editing
    const content = section?.content;
    if (typeof content === 'string') {
      setEditContent(content);
    } else if (typeof content === 'object') {
      setEditContent(JSON.stringify(content, null, 2));
    } else {
      setEditContent(String(content || ''));
    }
    setEditing(true);
  };

  const handleSave = async () => {
    if (!editContent.trim() || saving) return;
    setSaving(true);
    try {
      await onUpdateContent(sectionNumber, { content: editContent });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditContent('');
  };

  if (!section) return null;

  return (
    <div
      className={`rounded-xl border overflow-hidden cursor-pointer transition-all ${
        isActive
          ? 'bg-primary-50/40 border-primary-200 shadow-sm'
          : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
      onClick={() => onSelect && onSelect(sectionNumber)}
    >
      {/* Section header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">
            {sectionNumber}. {section.title}
          </h3>
          {isEdited && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded">
              Edited
            </span>
          )}
          {openComments > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-700 rounded">
              {openComments} comment{openComments !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {!editing && onUpdateContent && (
          <button
            onClick={(e) => { e.stopPropagation(); handleStartEdit(); }}
            className="text-xs text-gray-400 hover:text-primary-600 transition-colors px-2 py-1"
          >
            Edit
          </button>
        )}
      </div>

      {/* Section content */}
      <div className="px-6 py-4">
        {editing ? (
          <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={12}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-0 font-mono resize-y"
            />
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <ProposalSectionView section={section} hideTitle />
        )}

        {/* Comments thread — stop propagation so interactions don't trigger section select */}
        <div onClick={(e) => e.stopPropagation()}>
        <SectionCommentThread
          comments={comments}
          sectionNumber={sectionNumber}
          onAddComment={onAddComment}
          onDeleteComment={onDeleteComment}
          onRequestRevision={onRequestRevision}
          loading={commentLoading}
        />

        {/* Client comments */}
        {clientComments.filter((c) => c.section_number === sectionNumber).length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Client Comments</p>
            <div className="space-y-2">
              {clientComments
                .filter((c) => c.section_number === sectionNumber)
                .map((c) => (
                  <div key={c.id} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                    <p className="text-xs font-semibold text-blue-900 mb-0.5">{c.author_name || 'Client'}</p>
                    <p className="text-sm text-blue-800">{c.comment}</p>
                  </div>
                ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProposalSectionCard;
